<script>
import { format, parseISO } from 'date-fns';
import { required, url } from '@vuelidate/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import { isValidURL } from '../helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';
import { useVuelidate } from '@vuelidate/core';
import { emitter } from 'shared/helpers/mitt';

import NextButton from 'dashboard/components-next/button/Button.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  components: {
    MultiselectDropdown,
    HelperTextPopup,
    NextButton,
  },
  props: {
    label: { type: String, required: true },
    description: { type: String, default: '' },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: String, default: 'text' },
    attributeRegex: {
      type: String,
      default: null,
    },
    regexCue: { type: String, default: null },
    attributeKey: { type: String, required: true },
    contactId: { type: Number, default: null },
  },
  emits: ['update', 'delete', 'copy'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
    };
  },
  computed: {
    displayValue() {
      if (this.isAttributeTypeDate) {
        return this.value
          ? new Date(this.value || new Date()).toLocaleDateString()
          : '---';
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      return this.hasValue ? this.value : '---';
    },
    formattedValue() {
      return this.isAttributeTypeDate
        ? format(this.value ? new Date(this.value) : new Date(), DATE_FORMAT)
        : this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      const id = this.values.indexOf(this.editedValue) + 1;
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.attributeType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.attributeType === 'list';
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
    isAttributeTypeDate() {
      return this.attributeType === 'date';
    },
    hasValue() {
      return this.value !== null && this.value !== '';
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    isImageLink() {
      if (!this.isAttributeTypeLink || !this.hrefURL) {
        return false;
      }

      return /\.(apng|avif|bmp|gif|ico|jpe?g|png|svg|webp)(\?.*)?$/i.test(
        this.hrefURL
      );
    },
    linkLabel() {
      return this.$t('CUSTOM_ATTRIBUTES.OPEN_ATTRIBUTE', {
        title: this.label,
      });
    },
    notAttributeTypeCheckboxAndList() {
      return !this.isAttributeTypeCheckbox && !this.isAttributeTypeList;
    },
    inputType() {
      return this.isAttributeTypeLink ? 'url' : this.attributeType;
    },
    shouldShowErrorMessage() {
      return this.v$.editedValue.$error;
    },
    errorMessage() {
      if (this.v$.editedValue.url) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      if (!this.v$.editedValue.regexValidation) {
        return this.regexCue
          ? this.regexCue
          : this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_INPUT');
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
    },
    contactId() {
      // Fix to solve validation not resetting when contactId changes in contact page
      this.v$.$reset();
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    return {
      editedValue: {
        required,
        regexValidation: value => {
          return !(
            this.attributeRegex && !getRegexp(this.attributeRegex).test(value)
          );
        },
      },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    emitter.on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      if (this.$refs.inputfield) {
        this.$refs.inputfield.focus();
      }
    },
    onClickAway() {
      this.v$.$reset();
      this.isEditing = false;
    },
    onEdit() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    onUpdateListValue(value) {
      if (value) {
        this.editedValue = value.name;
        this.onUpdate();
      }
    },
    onUpdate() {
      const updatedValue =
        this.attributeType === 'date'
          ? parseISO(this.editedValue)
          : this.editedValue;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.v$.$reset();
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
  },
};
</script>

<template>
  <div>
    <div class="flex items-center justify-between gap-2 px-3 py-2.5 group">
      <div class="flex items-center flex-shrink-0 min-w-0 gap-2.5">
        <span class="flex-shrink-0 i-lucide-tag size-4 text-n-slate-11" />
        <span
          class="inline-flex items-center gap-1.5 text-sm truncate"
          :class="v$.editedValue.$error ? 'text-n-ruby-11' : 'text-n-slate-12'"
        >
          {{ label }}
          <HelperTextPopup v-if="description" :message="description" />
        </span>
      </div>

      <div
        v-if="isEditing && notAttributeTypeCheckboxAndList"
        v-on-clickaway="onClickAway"
        class="flex items-center justify-end flex-1 min-w-0 gap-1"
      >
        <input
          ref="inputfield"
          v-model="editedValue"
          :type="inputType"
          class="!h-8 !mb-0 !text-sm"
          autofocus="true"
          :class="{ error: v$.editedValue.$error }"
          @blur="v$.editedValue.$touch"
          @keyup.enter="onUpdate"
        />
        <NextButton sm icon="i-lucide-check" @click="onUpdate" />
      </div>

      <MultiselectDropdown
        v-else-if="isAttributeTypeList"
        plain
        :options="listOptions"
        :selected-item="selectedItem"
        :has-thumbnail="false"
        :multiselector-placeholder="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
        "
        :no-search-result="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
        "
        :input-placeholder="
          $t(
            'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
          )
        "
        @select="onUpdateListValue"
      />

      <input
        v-else-if="isAttributeTypeCheckbox"
        v-model="editedValue"
        type="checkbox"
        class="!my-0 flex-shrink-0"
        @change="onUpdate"
      />

      <div v-else class="flex items-center justify-end flex-1 min-w-0 gap-1">
        <NextButton
          v-if="showActions && hasValue"
          v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
          xs
          slate
          ghost
          icon="i-lucide-clipboard"
          class="flex-shrink-0 hidden group-hover:flex"
          @click="onCopy"
        />
        <NextButton
          v-if="showActions && hasValue"
          v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
          xs
          slate
          ghost
          icon="i-lucide-trash-2"
          class="flex-shrink-0 hidden group-hover:flex"
          @click="onDelete"
        />
        <button
          type="button"
          class="flex items-center min-w-0 gap-1.5"
          @click="onEdit"
        >
          <img
            v-if="isImageLink"
            :src="hrefURL"
            :alt="label"
            class="object-cover border rounded-md size-6 border-n-weak bg-n-alpha-2"
          />
          <span
            v-else
            class="text-sm truncate text-n-slate-11 group-hover:text-n-slate-12"
            :class="{ 'text-n-brand': isAttributeTypeLink }"
          >
            {{ isAttributeTypeLink ? linkLabel : displayValue }}
          </span>
          <span
            class="flex-shrink-0 i-lucide-chevron-right size-3.5 text-n-slate-9"
          />
        </button>
      </div>
    </div>
    <span
      v-if="shouldShowErrorMessage && isEditing"
      class="block px-3 pb-2 -mt-1 text-sm font-normal text-n-ruby-11"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>
